import { ReactNode } from 'react';
import { ModernErrorBoundary } from './ModernErrorBoundary';
import {
  PageErrorFallback,
  AuthErrorFallback,
  DashboardErrorFallback,
  ApiErrorFallback,
  CompactErrorFallback,
} from './RouteErrorFallbacks';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

// Page-level error boundary
export function PageErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ModernErrorBoundary
      level="page"
      fallback={(error, retry, reset) => (
        <PageErrorFallback error={error} retry={retry} reset={reset} />
      )}
    >
      {children}
    </ModernErrorBoundary>
  );
}

// Authentication error boundary
export function AuthErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ModernErrorBoundary
      level="page"
      fallback={(error, retry, reset) => (
        <AuthErrorFallback error={error} retry={retry} reset={reset} />
      )}
    >
      {children}
    </ModernErrorBoundary>
  );
}

// Dashboard/protected routes error boundary
export function DashboardErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ModernErrorBoundary
      level="section"
      fallback={(error, retry, reset) => (
        <DashboardErrorFallback error={error} retry={retry} reset={reset} />
      )}
    >
      {children}
    </ModernErrorBoundary>
  );
}

// API/data loading error boundary
export function DataErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ModernErrorBoundary
      level="component"
      fallback={(error, retry, reset) => (
        <ApiErrorFallback error={error} retry={retry} reset={reset} />
      )}
    >
      {children}
    </ModernErrorBoundary>
  );
}

// Compact error boundary for small components
export function CompactErrorBoundary({ children }: RouteErrorBoundaryProps) {
  return (
    <ModernErrorBoundary
      level="component"
      fallback={(error, retry, reset) => (
        <CompactErrorFallback error={error} retry={retry} reset={reset} />
      )}
    >
      {children}
    </ModernErrorBoundary>
  );
}

// Generic error boundary with custom fallback
interface GenericErrorBoundaryProps extends RouteErrorBoundaryProps {
  fallback?: (error: Error, retry: () => void, reset: () => void) => ReactNode;
  level?: 'page' | 'section' | 'component';
  onError?: (error: Error) => void;
}

export function GenericErrorBoundary({
  children,
  fallback,
  level = 'component',
  onError,
}: GenericErrorBoundaryProps) {
  return (
    <ModernErrorBoundary
      level={level}
      fallback={fallback}
      onError={onError ? (error) => onError(error) : undefined}
    >
      {children}
    </ModernErrorBoundary>
  );
}
